import { useState, useEffect, useRef } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import api from '../../services/api'
import toast from 'react-hot-toast'
import MarkdownContent from '../../components/MarkdownContent'
import {
  BookOpen,
  Clock,
  FileText,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  ArrowRight,
  Target,
  Calendar,
  User,
  Lock,
  Play,
  Upload,
  Award,
  TrendingUp,
  Sparkles,
  ExternalLink,
  Circle,
  Flag,
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'

export default function CourseDetails() {
  const { courseId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const targetLessonId = searchParams.get('lesson') // Phase 4B: ?lesson=ID for deep-linking
  const lessonRefs = useRef({}) // Phase 4B: lessonId → DOM element for scrolling
  const [course, setCourse] = useState(null)
  const [modules, setModules] = useState([])
  const [lessons, setLessons] = useState([])
  const [assignments, setAssignments] = useState([])
  const [expandedModules, setExpandedModules] = useState(new Set())
  const [gradeSummary, setGradeSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accessDenied, setAccessDenied] = useState(false)
  const [accessMessage, setAccessMessage] = useState('')
  const [paymentProgress, setPaymentProgress] = useState(null)
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [showGradeBreakdown, setShowGradeBreakdown] = useState(false)
  const [selectedAssignment, setSelectedAssignment] = useState(null)
  const [submissionData, setSubmissionData] = useState({ submission_url: '', submission_text: '' })
  const [submitting, setSubmitting] = useState(false)
  // Phase 4A: lesson progress tracking
  const [lessonProgress, setLessonProgress] = useState({}) // { lessonId: { status, completed_at } }
  const [completingLessonId, setCompletingLessonId] = useState(null) // lesson currently being marked complete
  // Phase 2.1B: track which lessons we have already recorded access for in this session
  const recordedLessonIds = useRef(new Set())

  useEffect(() => {
    fetchCourseDetails()
  }, [courseId])

  const fetchCourseDetails = async () => {
    try {
      setLoading(true)
      setAccessDenied(false)
      setAccessMessage('')
      setPaymentProgress(null)

      // Get course info and modules (structure); unlock flags from student API
      const [courseRes, modulesRes] = await Promise.all([
        api.get(`/courses/${courseId}`),
        api.get(`/courses/${courseId}/modules`)
      ])

      setCourse(courseRes.data)
      const modulesArray = modulesRes.data.modules || []

      // Phase 4A: Default-expand the first module (will be refined after progress loads)
      if (modulesArray.length > 0) {
        setExpandedModules(new Set([modulesArray[0].id]))
      }

      try {
        const [lessonsRes, assignmentsRes, gradeRes, progressRes] = await Promise.all([
          api.get(`/students/courses/${courseId}/lessons`),
          api.get(`/students/courses/${courseId}/assignments`),
          api.get(`/students/courses/${courseId}/grade-summary`),
          api.get(`/progress/course/${courseId}`).catch(() => null), // non-fatal if progress API unavailable
        ])

        const payload = lessonsRes.data
        const lessonList = Array.isArray(payload) ? payload : (payload.lessons || [])
        const progress = Array.isArray(payload) ? null : payload.payment_progress

        setLessons(lessonList)
        setPaymentProgress(progress)

        // Phase 4A: Build lesson progress map from /progress/course/:courseId response
        const progressMap = {}
        if (progressRes?.data?.lesson_progress) {
          progressRes.data.lesson_progress.forEach(lp => {
            progressMap[lp.lesson_id] = {
              status: lp.status,
              completed_at: lp.completed_at,
            }
          })
        }
        setLessonProgress(progressMap)

        const unlockMap = Object.fromEntries(
          lessonList.map(l => [l.id, l.is_unlocked !== false])
        )
        const enrichedModules = modulesArray.map(m => ({
          ...m,
          lessons: (m.lessons || []).map(lesson => ({
            ...lesson,
            is_unlocked: unlockMap[lesson.id] ?? false
          }))
        }))
        setModules(enrichedModules)

        if (progress && progress.unlock_percent === 0) {
          setAccessDenied(true)
          setAccessMessage(
            progress.deposit_met === false
              ? `Pay at least ${progress.deposit_required_percent}% of course tuition (${Number(progress.amount * 0.5).toLocaleString()} UGX minimum) to unlock lessons. The 50,000 UGX registration fee only activates your portal login.`
              : 'Complete tuition payment to unlock lessons.'
          )
        }

        setAssignments(assignmentsRes.data)
        setGradeSummary(gradeRes.data)
      } catch (err) {
        if (err.response?.status === 403) {
          setAccessDenied(true)
          setAccessMessage(err.response?.data?.error || 'Access restricted for this course.')
          setModules(modulesArray)
        }
      }
    } catch (error) {
      console.error('Error fetching course details:', error)
      toast.error('Failed to load course details')
    } finally {
      setLoading(false)
    }
  }

  // Phase 2.1B: Record lesson access telemetry.
  // This is best-effort; telemetry failures must never block the student.
  const recordLessonAccess = async (lessonId) => {
    if (!lessonId || recordedLessonIds.current.has(lessonId)) return
    recordedLessonIds.current.add(lessonId)
    try {
      await api.post(`/progress/lesson/${lessonId}/access`)
    } catch (error) {
      // Non-fatal: continue lesson delivery even if telemetry fails.
      console.warn('[CourseDetails] Lesson access telemetry failed:', error.message)
    }
  }

  // Phase 4B: Auto-expand module and scroll to lesson when navigated via ?lesson=ID
  useEffect(() => {
    if (!targetLessonId || modules.length === 0 || loading) return
    // Phase 2.1B: record lesson access for deep-linked lesson
    recordLessonAccess(parseInt(targetLessonId, 10))
    // Find the module containing this lesson
    const targetModule = modules.find(m =>
      (m.lessons || []).some(l => l.id === parseInt(targetLessonId, 10))
    )
    if (targetModule) {
      setExpandedModules(prev => new Set([...prev, targetModule.id]))
      // Scroll to the lesson after expansion renders
      setTimeout(() => {
        const el = lessonRefs.current[parseInt(targetLessonId, 10)]
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 300)
    }
  }, [targetLessonId, modules, loading])

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev)
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId)
      } else {
        newSet.add(moduleId)
      }
      return newSet
    })
  }

  const toggleAllModules = () => {
    if (expandedModules.size === modules.length) {
      setExpandedModules(new Set())
    } else {
      setExpandedModules(new Set(modules.map(m => m.id)))
    }
  }

  // Phase 4A: Mark a lesson as complete via the existing progress API
  const handleMarkLessonComplete = async (lessonId) => {
    // Don't re-call if already completed
    if (lessonProgress[lessonId]?.status === 'completed') return
    // Don't call if already in-flight
    if (completingLessonId === lessonId) return

    setCompletingLessonId(lessonId)
    try {
      const res = await api.post(`/progress/lesson/${lessonId}/complete`)
      // Update local progress state immediately (no full refresh needed)
      setLessonProgress(prev => ({
        ...prev,
        [lessonId]: {
          status: 'completed',
          completed_at: res.data?.progress?.completed_at || new Date().toISOString(),
        }
      }))
      toast.success('Lesson marked complete')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to mark lesson complete')
    } finally {
      setCompletingLessonId(null)
    }
  }

  // Phase 4A: Compute overall course progress from lessonProgress + lessons
  const totalLessonsCount = lessons.length
  const completedLessonsCount = lessons.filter(
    l => lessonProgress[l.id]?.status === 'completed'
  ).length
  const coursePct = totalLessonsCount > 0
    ? Math.round((completedLessonsCount / totalLessonsCount) * 100)
    : 0
  const remainingLessons = totalLessonsCount - completedLessonsCount

  // Phase 4A: Compute per-module (week) status
  // Returns: { status: 'completed'|'in_progress'|'upcoming'|'locked', completedCount, totalCount, isCurrent }
  const getModuleStatus = (module) => {
    const moduleLessons = lessons.filter(l => l.module_id === module.id)
    const totalCount = moduleLessons.length
    const completedCount = moduleLessons.filter(
      l => lessonProgress[l.id]?.status === 'completed'
    ).length
    const allLocked = moduleLessons.every(l => l.is_unlocked === false)

    if (allLocked && totalCount > 0) {
      return { status: 'locked', completedCount, totalCount, isCurrent: false }
    }
    if (completedCount === totalCount && totalCount > 0) {
      return { status: 'completed', completedCount, totalCount, isCurrent: false }
    }
    if (completedCount > 0) {
      return { status: 'in_progress', completedCount, totalCount, isCurrent: false }
    }
    return { status: 'upcoming', completedCount, totalCount, isCurrent: false }
  }

  // Phase 4A: Determine the "current" week — the week containing the first
  // incomplete, unlocked lesson. If all complete, no current week.
  const currentModuleId = (() => {
    if (totalLessonsCount === 0) return null
    if (completedLessonsCount === totalLessonsCount) return null // course complete

    // Find first incomplete unlocked lesson
    const sortedModules = [...modules].sort((a, b) =>
      (a.module_number || 0) - (b.module_number || 0)
    )
    for (const mod of sortedModules) {
      const moduleLessons = (mod.lessons || []).filter(
        l => l.is_unlocked !== false && lessonProgress[l.id]?.status !== 'completed'
      )
      if (moduleLessons.length > 0) return mod.id
    }
    return null
  })()

  const isCourseComplete = totalLessonsCount > 0 && completedLessonsCount === totalLessonsCount

  // Phase 4B: Build an ordered flat list of all lessons across modules for prev/next navigation
  const orderedLessons = (() => {
    const sorted = [...modules].sort((a, b) => (a.module_number || 0) - (b.module_number || 0))
    const list = []
    sorted.forEach(mod => {
      (mod.lessons || []).forEach(lesson => {
        list.push({
          ...lesson,
          module_id: mod.id,
          module_number: mod.module_number,
          module_title: mod.title,
          week_number: mod.week_number,
        })
      })
    })
    return list
  })()

  // Phase 4B: Get previous/next lesson for a given lesson ID
  const getAdjacentLesson = (lessonId, direction) => {
    const idx = orderedLessons.findIndex(l => l.id === lessonId)
    if (idx === -1) return null
    if (direction === 'prev') return idx > 0 ? orderedLessons[idx - 1] : null
    if (direction === 'next') return idx < orderedLessons.length - 1 ? orderedLessons[idx + 1] : null
    return null
  }

  // Phase 4B: Navigate to a lesson (updates URL param, expands module, scrolls)
  const navigateToLesson = (lesson) => {
    if (!lesson) return
    // Phase 2.1B: record telemetry when the student explicitly opens a lesson.
    recordLessonAccess(lesson.id)
    const mod = modules.find(m => m.id === lesson.module_id)
    if (mod) {
      setExpandedModules(prev => new Set([...prev, mod.id]))
    }
    setSearchParams({ lesson: lesson.id })
    setTimeout(() => {
      const el = lessonRefs.current[lesson.id]
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }

  const handleSubmitAssignment = async (e) => {
    e.preventDefault()
    if (!selectedAssignment) return

    setSubmitting(true)
    try {
      await api.post(`/students/assignments/${selectedAssignment.id}/submit`, submissionData)
      toast.success('Assignment submitted successfully!')
      setShowSubmitModal(false)
      setSubmissionData({ submission_url: '', submission_text: '' })
      setSelectedAssignment(null)
      // Refresh assignments
      const assignmentsRes = await api.get(`/students/courses/${courseId}/assignments`)
      setAssignments(assignmentsRes.data)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit assignment')
    } finally {
      setSubmitting(false)
    }
  }

  // Build a map of lessons by module
  const getLessonsForModule = (moduleId) => {
    return lessons.filter(l => l.module_id === moduleId)
  }

  // Get assignment for a lesson
  const getAssignmentsForLesson = (lessonId) => {
    return assignments.filter(a => a.lesson_id === lessonId)
  }

  // Phase 4C: Detect capstone assignment (Week 6 / final module)
  const isCapstoneAssignment = (assignment) => {
    if (!assignment) return false
    const lesson = lessons.find(l => l.id === assignment.lesson_id)
    if (!lesson) return false
    const mod = modules.find(m => m.id === lesson.module_id)
    if (!mod) return false
    // Week 6 is the capstone week for AI for Creatives
    return mod.week_number === 6 || mod.module_number === Math.max(...modules.map(m => m.module_number || 0))
  }

  if (loading) return <LoadingSpinner label="Loading course" />

  if (!course) return (
    <div className="cinema-empty">
      <BookOpen size={36} className="mx-auto mb-4 text-cinema-muted/20" />
      <p className="font-sans text-sm text-cinema-muted mb-4">Course not found</p>
      <Link to="/student/courses" className="cinema-btn-primary text-xs px-5 py-2.5 min-h-0 inline-flex">
        Back to My Courses
      </Link>
    </div>
  )

  const totalLessons = modules.reduce((sum, mod) => sum + (mod.lessons?.length || 0), 0)
  const completedAssignments = assignments.filter(a => a.submission_id).length
  const totalAssignments = assignments.length

  const gradeAccent = (pct) => {
    if (pct === null) return { text: 'text-cinema-muted', bar: 'bg-white/20', border: 'border-white/10', glow: 'rgba(255,255,255,0.04)' }
    if (pct >= 75) return { text: 'text-green-400',  bar: 'bg-green-500',  border: 'border-green-500/20',  glow: 'rgba(34,197,94,0.06)' }
    if (pct >= 50) return { text: 'text-cinema-glow', bar: 'bg-cinema-glow', border: 'border-cinema-glow/20', glow: 'rgba(238,141,33,0.06)' }
    return          { text: 'text-red-400',   bar: 'bg-red-500',   border: 'border-red-500/20',   glow: 'rgba(239,68,68,0.06)' }
  }

  const inputCls = "w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-2.5 focus:outline-none focus:border-cinema-glow/40 transition-colors placeholder:text-cinema-muted/40"

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="cinema-page-header flex items-start gap-3">
        <Link to="/student/courses" className="p-2 cinema-glass border border-white/8 text-cinema-muted hover:text-white hover:border-white/20 transition-all mt-0.5 shrink-0">
          <ArrowLeft size={16} />
        </Link>
        <div className="flex-1 min-w-0">
          <span className="cinema-label block mb-0.5">Student Portal</span>
          <h1 className="font-display text-xl text-white font-light truncate">{course.title}</h1>
          <p className="font-sans text-xs text-cinema-muted mt-0.5">{course.course_code}</p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-8">
        {[
          { label: 'Modules',     value: modules.length },
          { label: 'Lessons',     value: totalLessons },
          { label: 'Assignments', value: `${completedAssignments}/${totalAssignments}` },
          { label: 'Duration',    value: `${course.duration_weeks || course.duration || '—'}w` },
        ].map(({ label, value }) => (
          <div key={label} className="cinema-stat">
            <span className="cinema-stat-label">{label}</span>
            <span className="cinema-stat-value">{value}</span>
          </div>
        ))}
      </div>

      {/* Trainer */}
      {course.trainer_name && (
        <div className="flex items-center gap-3">
          <div className="text-cinema-muted"><User size={14} /></div>
          <div>
            <p className="cinema-label">Instructor</p>
            <p className="font-sans text-xs text-white mt-0.5">{course.trainer_name}</p>
          </div>
        </div>
      )}

      {/* Description */}
      {course.description && (
        <div className="pt-8 border-t border-white/5">
          <div className="cinema-section-header">
            <h2 className="cinema-section-title">About This Course</h2>
          </div>
          <p className="font-sans text-sm sm:text-base text-cinema-soft leading-relaxed">{course.description}</p>
        </div>
      )}

      {/* Grade summary */}
      {gradeSummary && !accessDenied && (
        <div className="pt-8 border-t border-white/5">
          <button
            onClick={() => setShowGradeBreakdown(v => !v)}
            className="w-full flex flex-wrap items-center justify-between gap-3 mb-4"
          >
            <div className="flex items-center gap-3">
              <div className={gradeAccent(gradeSummary.summary.percentage).text}><Award size={18} /></div>
              <div className="text-left">
                <p className="cinema-label">Overall Grade</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className={`font-display text-xl font-semibold ${gradeAccent(gradeSummary.summary.percentage).text}`}>
                    {gradeSummary.summary.percentage !== null ? `${gradeSummary.summary.percentage}%` : 'No grades yet'}
                  </p>
                  {gradeSummary.summary.graded > 0 && (
                    <span className="font-sans text-xs font-mono text-cinema-muted">{gradeSummary.summary.earned} / {gradeSummary.summary.possible} pts</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-cinema-muted">
              <span className="font-sans text-xs sm:text-sm font-medium">{gradeSummary.summary.graded} of {gradeSummary.summary.totalAssignments} graded</span>
              <TrendingUp size={14} className={`transition-transform ${showGradeBreakdown ? 'rotate-180' : ''}`} />
            </div>
          </button>

          {gradeSummary.summary.percentage !== null && (
            <div className="mb-4">
              <div className="w-full bg-white/5 h-1.5">
                <div className={`h-1.5 transition-all ${gradeAccent(gradeSummary.summary.percentage).bar}`} style={{ width: `${gradeSummary.summary.percentage}%` }} />
              </div>
            </div>
          )}

          {showGradeBreakdown && (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/6">
                      <th className="px-3 py-2.5 text-left cinema-label font-normal">Assignment</th>
                      <th className="px-3 py-2.5 text-left cinema-label font-normal">Module</th>
                      <th className="px-3 py-2.5 text-right cinema-label font-normal">Score</th>
                      <th className="px-3 py-2.5 text-right cinema-label font-normal">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/4">
                    {gradeSummary.assignments.map(a => {
                      const pct = a.score !== null ? Math.round((a.score / a.max_score) * 100) : null
                      return (
                        <tr key={a.id} className="hover:bg-white/[0.02] transition-colors">
                          <td className="px-3 py-3 font-sans text-xs sm:text-sm text-white font-medium">{a.title}</td>
                          <td className="px-3 py-3 font-sans text-xs text-cinema-muted">{a.module_title}</td>
                          <td className="px-3 py-3 text-right">
                            {a.score !== null
                              ? <span className={`font-sans text-xs sm:text-sm font-medium ${gradeAccent(pct).text}`}>{a.score} / {a.max_score}</span>
                              : <span className="font-sans text-xs sm:text-sm text-cinema-muted">— / {a.max_score}</span>}
                          </td>
                          <td className="px-3 py-3 text-right">
                            {a.score !== null
                              ? <span className="px-2.5 py-0.5 text-xs font-sans font-medium bg-green-500/10 text-green-400 border border-green-500/25 rounded-sm">Graded</span>
                              : a.submission_id
                              ? <span className="px-2.5 py-0.5 text-xs font-sans font-medium bg-yellow-500/10 text-yellow-400 border border-yellow-500/25 rounded-sm">Pending</span>
                              : <span className="px-2.5 py-0.5 text-xs font-sans font-medium bg-white/5 text-cinema-muted border border-white/10 rounded-sm">Not submitted</span>}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="sm:hidden divide-y divide-white/4">
                {gradeSummary.assignments.map(a => {
                  const pct = a.score !== null ? Math.round((a.score / a.max_score) * 100) : null
                  return (
                    <div key={a.id} className="py-3 space-y-1.5">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-sans text-sm text-white font-medium flex-1 min-w-0">{a.title}</p>
                        {a.score !== null
                          ? <span className="px-2 py-0.5 text-xs font-sans font-medium bg-green-500/10 text-green-400 border border-green-500/25 rounded-sm shrink-0">Graded</span>
                          : a.submission_id
                          ? <span className="px-2 py-0.5 text-xs font-sans font-medium bg-yellow-500/10 text-yellow-400 border border-yellow-500/25 rounded-sm shrink-0">Pending</span>
                          : <span className="px-2 py-0.5 text-xs font-sans font-medium bg-white/5 text-cinema-muted border border-white/10 rounded-sm shrink-0">Not submitted</span>}
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-sans text-xs text-cinema-muted">{a.module_title}</span>
                        {a.score !== null
                          ? <span className={`font-sans text-sm font-medium ${gradeAccent(pct).text}`}>{a.score} / {a.max_score}</span>
                          : <span className="font-sans text-xs text-cinema-muted">— / {a.max_score}</span>}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Payment progress banner */}
      {paymentProgress && paymentProgress.amount > 0 && (
        <div className={`pl-4 border-l-2 ${paymentProgress.unlock_percent === 0 ? 'border-yellow-500/40' : 'border-blue-500/40'}`}>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <p className="font-sans text-sm text-white font-medium">Course Tuition: {paymentProgress.percent_paid}% paid</p>
              <p className="font-sans text-xs sm:text-sm text-cinema-muted mt-1 leading-relaxed">
                {paymentProgress.unlock_percent === 0
                  ? `Pay at least ${paymentProgress.deposit_required_percent}% of course tuition to unlock lessons.`
                  : `${paymentProgress.lessons_unlocked} of ${paymentProgress.lessons_total} lessons unlocked.`}
                {paymentProgress.balance_due > 0 && <span> Balance: {Number(paymentProgress.balance_due).toLocaleString()} UGX.</span>}
                {!paymentProgress.certificate_eligible && <span> Certificates unlock at zero balance.</span>}
              </p>
            </div>
            <Link to="/student/payments" className="cinema-btn-primary text-xs sm:text-sm px-4 py-2 min-h-0 shrink-0">Pay Tuition</Link>
          </div>
          <div className="w-full bg-white/5 h-1.5">
            <div className="h-1.5 bg-cinema-glow transition-all" style={{ width: `${paymentProgress.percent_paid}%` }} />
          </div>
        </div>
      )}

      {/* Access denied banner */}
      {accessDenied && !paymentProgress?.amount && (
        <div className="pl-4 border-l-2 border-yellow-500/40 flex flex-wrap items-center gap-3">
          <Lock size={16} className="text-yellow-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-sans text-sm text-white font-medium">Course Access Restricted</p>
            <p className="font-sans text-xs sm:text-sm text-cinema-muted mt-0.5">{accessMessage || 'Complete tuition payment to unlock lessons and assignments.'}</p>
          </div>
          <Link to="/student/payments" className="cinema-btn-primary text-xs sm:text-sm px-4 py-2 min-h-0 shrink-0">View Payments</Link>
        </div>
      )}

      {/* Phase 4A: Course Progress Overview */}
      {totalLessonsCount > 0 && (
        <div className="pt-8 border-t border-white/5">
          <div className="cinema-section-header">
            <h2 className="cinema-section-title">Course Progress</h2>
          </div>

          {/* Progress summary */}
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
            <p className="font-display text-2xl sm:text-3xl text-white font-light">
              {completedLessonsCount} <span className="text-cinema-muted text-base sm:text-lg">/ {totalLessonsCount}</span>
            </p>
            <p className="font-sans text-xs sm:text-sm text-cinema-muted uppercase tracking-wider font-medium">
              lessons complete
            </p>
            <span className={`font-display text-lg sm:text-xl font-semibold ${
              coursePct === 100 ? 'text-green-400' : 'text-cinema-glow'
            }`}>
              {coursePct}%
            </span>
            {isCourseComplete && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-sans font-semibold uppercase tracking-wide bg-green-500/10 text-green-400 border border-green-500/25 rounded-sm">
                <CheckCircle2 size={12} /> Course Complete
              </span>
            )}
          </div>

          {/* Progress bar */}
          <div className="w-full h-2 bg-white/5 mb-3">
            <div
              className={`h-2 transition-all duration-700 ${coursePct === 100 ? 'bg-green-500' : 'bg-cinema-glow'}`}
              style={{ width: `${coursePct}%` }}
            />
          </div>

          {/* Remaining info */}
          {remainingLessons > 0 && (
            <p className="font-sans text-xs text-cinema-muted">
              Remaining: {remainingLessons} lesson{remainingLessons !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      )}

      {/* Phase 4A: Week Roadmap — compact visual progression */}
      {modules.length > 0 && (
        <div className="pt-8 border-t border-white/5">
          <div className="cinema-section-header">
            <h2 className="cinema-section-title">Your 6-Week Journey</h2>
          </div>

          {/* Week progression line — desktop only */}
          <div className="hidden sm:flex items-center gap-1 mb-6 max-w-2xl">
            {modules.map((mod, i) => {
              const status = getModuleStatus(mod)
              const isCurrent = mod.id === currentModuleId
              const isCapstone = mod.module_number === 6
              const dotColor = status.status === 'completed'
                ? 'bg-green-500 border-green-500 text-white'
                : isCurrent
                ? 'bg-cinema-glow border-cinema-glow text-white'
                : status.status === 'locked'
                ? 'bg-white/3 border-white/10 text-cinema-muted/50'
                : isCapstone
                ? 'bg-cinema-gold/15 border-cinema-gold/40 text-cinema-gold'
                : 'bg-white/5 border-white/10 text-cinema-muted'
              return (
                <div key={mod.id} className="flex items-center flex-1 last:flex-none">
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-mono font-semibold shrink-0 border ${dotColor}`}>
                    {status.status === 'completed' ? <CheckCircle2 size={13} /> : mod.module_number}
                  </div>
                  {i < modules.length - 1 && (
                    <div className={`flex-1 h-px mx-1 ${status.status === 'completed' ? 'bg-green-500/40' : 'bg-white/10'}`} />
                  )}
                </div>
              )
            })}
          </div>

          {/* Week list — compact */}
          <div className="space-y-0">
            {modules.map((module) => {
              const status = getModuleStatus(module)
              const isCurrent = module.id === currentModuleId
              const isCapstone = module.module_number === 6
              const moduleLessons = lessons.filter(l => l.module_id === module.id)

              return (
                <div
                  key={module.id}
                  className={`border-l-2 pl-4 sm:pl-6 py-4 relative ${
                    isCurrent ? 'border-cinema-glow' :
                    status.status === 'completed' ? 'border-green-500/40' :
                    isCapstone ? 'border-cinema-gold/40' :
                    'border-white/8'
                  }`}
                >
                  {/* You are here indicator */}
                  {isCurrent && (
                    <span className="absolute -left-2 top-4 px-2 py-0.5 text-[0.6rem] font-sans font-semibold uppercase tracking-wider bg-cinema-glow text-white rounded-sm whitespace-nowrap">
                      You are here
                    </span>
                  )}

                  <div className="flex items-start gap-3">
                    {/* Week number / status icon */}
                    <span className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-mono font-semibold shrink-0 border mt-0.5 ${
                      status.status === 'completed'
                        ? 'bg-green-500/15 border-green-500/40 text-green-400'
                        : isCurrent
                        ? 'bg-cinema-glow/15 border-cinema-glow/50 text-cinema-glow'
                        : status.status === 'locked'
                        ? 'bg-white/3 border-white/10 text-cinema-muted/50'
                        : isCapstone
                        ? 'bg-cinema-gold/15 border-cinema-gold/40 text-cinema-gold'
                        : 'bg-white/5 border-white/10 text-cinema-muted'
                    }`}>
                      {status.status === 'completed' ? <CheckCircle2 size={13} /> :
                       status.status === 'locked' ? <Lock size={11} /> :
                       module.module_number}
                    </span>

                    <div className="flex-1 min-w-0">
                      {/* Week title + status */}
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-[0.65rem] uppercase tracking-[0.15em] text-cinema-muted font-sans font-semibold">
                          {isCapstone ? `Week ${module.module_number} · Final Capstone` : `Week ${module.module_number}`}
                        </p>
                        {/* Status badge */}
                        {status.status === 'completed' && (
                          <span className="text-[0.6rem] font-sans font-semibold uppercase tracking-wide text-green-400">
                            {status.completedCount}/{status.totalCount} · Completed
                          </span>
                        )}
                        {status.status === 'in_progress' && (
                          <span className="text-[0.6rem] font-sans font-semibold uppercase tracking-wide text-cinema-glow">
                            {status.completedCount}/{status.totalCount} · In Progress
                          </span>
                        )}
                        {status.status === 'upcoming' && !isCurrent && (
                          <span className="text-[0.6rem] font-sans font-semibold uppercase tracking-wide text-cinema-muted">
                            {status.completedCount}/{status.totalCount} · Upcoming
                          </span>
                        )}
                        {status.status === 'locked' && (
                          <span className="text-[0.6rem] font-sans font-semibold uppercase tracking-wide text-cinema-muted/60">
                            Locked
                          </span>
                        )}
                      </div>

                      <h3 className="font-display text-sm sm:text-base text-white font-medium leading-tight mt-0.5">
                        {module.title}
                      </h3>

                      {/* Mini progress bar for in-progress weeks */}
                      {status.status === 'in_progress' && status.totalCount > 0 && (
                        <div className="w-full max-w-[200px] h-1 bg-white/5 mt-2">
                          <div className="h-1 bg-cinema-glow transition-all duration-500" style={{ width: `${(status.completedCount / status.totalCount) * 100}%` }} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Course Structure */}
      <div className="pt-8 border-t border-white/5">
        <div className="cinema-section-header flex items-center justify-between">
          <h2 className="cinema-section-title">Course Structure</h2>
          <button onClick={toggleAllModules} className="font-sans text-xs sm:text-sm text-cinema-glow hover:text-cinema-amber transition-colors font-medium">
            {expandedModules.size === modules.length ? 'Collapse All' : 'Expand All'}
          </button>
        </div>

        {modules.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen size={32} className="mx-auto mb-3 text-cinema-muted/20" />
            <p className="font-sans text-sm text-cinema-muted">No modules available yet</p>
          </div>
        ) : (
          <div className="divide-y divide-white/4">
            {modules.map((module, moduleIndex) => (
              <div key={module.id}>
                {/* Module header */}
                <button
                  onClick={() => toggleModule(module.id)}
                  className="w-full flex items-center justify-between py-4 hover:bg-white/[0.02] transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    {expandedModules.has(module.id)
                      ? <ChevronDown size={14} className="text-cinema-muted shrink-0" />
                      : <ChevronRight size={14} className="text-cinema-muted shrink-0" />}
                    <span className="w-6 h-6 flex items-center justify-center bg-cinema-glow/15 text-cinema-glow font-mono text-xs rounded-sm shrink-0 font-semibold">
                      {module.module_number || moduleIndex + 1}
                    </span>
                    <div>
                      <p className="font-sans text-sm sm:text-base text-white font-medium">{module.title}</p>
                      {module.description && (
                        <p className="font-sans text-xs text-cinema-muted hidden sm:block mt-0.5">{module.description}</p>
                      )}
                    </div>
                  </div>
                  <span className="hidden sm:flex items-center gap-1.5 font-sans text-xs text-cinema-muted shrink-0 font-medium">
                    <FileText size={12} /> {module.lessons?.length || 0} lessons
                  </span>
                </button>

                {/* Lessons */}
                {expandedModules.has(module.id) && module.lessons && module.lessons.length > 0 && (
                  <div className="border-t border-white/4 divide-y divide-white/3 bg-white/2">
                    {module.lessons.map((lesson, lessonIndex) => {
                      const lessonAssignments = getAssignmentsForLesson(lesson.id)
                      const isLocked = lesson.is_unlocked === false
                      const dayNumber = lesson.day_number || lesson.lesson_number || (lessonIndex + 1)
                      const totalLessons = lessons.length || 18
                      const totalModules = modules.length || 6
                      // Phase 4A: lesson completion state
                      const isLessonComplete = lessonProgress[lesson.id]?.status === 'completed'
                      const isCompleting = completingLessonId === lesson.id
                      const isCurrentLesson = !isLessonComplete && !isLocked && module.id === currentModuleId
                      return (
                        <div
                          key={lesson.id}
                          ref={el => { lessonRefs.current[lesson.id] = el }}
                          className={`px-4 sm:px-6 py-5 ${isLocked ? 'opacity-50' : 'hover:bg-white/[0.02]'} transition-colors scroll-mt-20`}
                        >
                          {/* Lesson header */}
                          <div className="flex items-start gap-3 mb-1">
                            <span className={`w-7 h-7 flex items-center justify-center rounded-sm text-xs font-mono mt-0.5 shrink-0 font-semibold ${
                              isLocked ? 'bg-white/5 text-cinema-muted' :
                              isLessonComplete ? 'bg-green-500/15 text-green-400' :
                              isCurrentLesson ? 'bg-cinema-glow/20 text-cinema-glow ring-1 ring-cinema-glow/30' :
                              'bg-cinema-glow/15 text-cinema-glow'
                            }`}>
                              {isLocked ? <Lock size={12} /> :
                               isLessonComplete ? <CheckCircle2 size={14} /> :
                               dayNumber}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="font-sans text-[10px] sm:text-xs text-cinema-muted uppercase tracking-wider font-medium flex items-center gap-2">
                                Day {dayNumber}
                                {isLessonComplete && (
                                  <span className="text-green-400 normal-case tracking-normal font-semibold">· Completed</span>
                                )}
                                {isCurrentLesson && !isLessonComplete && (
                                  <span className="text-cinema-glow normal-case tracking-normal font-semibold">· Current</span>
                                )}
                              </p>
                              <h3 className={`font-display text-base sm:text-lg font-medium mt-0.5 leading-snug ${
                                isLessonComplete ? 'text-cinema-soft' : 'text-white'
                              }`}>
                                {lesson.title}
                              </h3>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                                {lesson.lesson_type && (
                                  <span className="font-sans text-xs text-cinema-muted capitalize">{lesson.lesson_type}</span>
                                )}
                                {lesson.duration_minutes && (
                                  <span className="flex items-center gap-1 font-sans text-xs text-cinema-muted">
                                    <Clock size={11} /> {lesson.duration_minutes} min
                                  </span>
                                )}
                                <span className="font-sans text-xs text-cinema-muted/60">
                                  Week {module.module_number} of {totalModules} · Lesson {lesson.lesson_number || (lessonIndex + 1)} of {totalLessons}
                                </span>
                              </div>
                            </div>

                            {/* Phase 4A: Mark Lesson Complete button */}
                            {!isLocked && !isLessonComplete && (
                              <button
                                onClick={() => handleMarkLessonComplete(lesson.id)}
                                disabled={isCompleting}
                                className="shrink-0 mt-0.5 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans font-medium cinema-glass border border-cinema-glow/25 text-cinema-glow hover:bg-cinema-glow/10 hover:border-cinema-glow/40 transition-all disabled:opacity-50 min-h-[36px]"
                                title="Mark this lesson as complete"
                              >
                                {isCompleting ? (
                                  <><LoadingSpinner size="sm" inline /> Marking...</>
                                ) : (
                                  <><CheckCircle size={13} /> Mark Complete</>
                                )}
                              </button>
                            )}
                            {isLessonComplete && (
                              <div className="shrink-0 mt-0.5 flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans font-medium text-green-400">
                                <CheckCircle2 size={14} />
                                <span className="hidden sm:inline">Completed</span>
                              </div>
                            )}
                          </div>

                          {/* Lesson content — constrained reading width */}
                          {isLocked && (
                            <p className="font-sans text-xs text-yellow-500/80 mt-2 px-4 sm:px-0 sm:pl-10">Locked — pay more tuition to unlock</p>
                          )}
                          {lesson.description && !isLocked && (
                            <div className="mt-3 px-4 sm:px-0 sm:pl-10 max-w-[850px]">
                              <MarkdownContent content={lesson.description} />
                            </div>
                          )}

                          {/* Lesson meta links */}
                          <div className="flex flex-wrap items-center gap-3 mt-3 px-4 sm:px-0 sm:pl-10">
                            {lesson.content_type && (
                              <span className="flex items-center gap-1.5 font-sans text-xs text-cinema-muted">
                                <Play size={11} /> {lesson.content_type}
                              </span>
                            )}
                            {!isLocked && lesson.content_url && (
                              <a
                                href={lesson.content_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 font-sans text-xs text-cinema-glow hover:text-white transition-colors font-medium"
                              >
                                <ExternalLink size={12} /> Open lesson file
                              </a>
                            )}
                            {!isLocked && (lesson.teachingMaterials || []).map((material) => (
                              <a
                                key={material.id}
                                href={material.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 font-sans text-xs text-cinema-glow hover:text-white transition-colors font-medium"
                              >
                                <ExternalLink size={12} /> {material.title}
                              </a>
                            ))}
                            {!isLocked && (lesson.digitalLabResources || []).map((res) => (
                              <Link
                                key={res.id}
                                to={`/student/digital-lab/resources/${res.id}`}
                                className="inline-flex items-center gap-1.5 font-sans text-xs text-cinema-glow hover:text-white transition-colors font-medium"
                                title="Open in Digital Lab"
                              >
                                <Sparkles size={12} className="text-cinema-gold" />
                                <span>Digital Lab: {res.title}{res.creator ? ` — ${res.creator}` : ''}</span>
                                <ArrowRight size={11} className="text-cinema-muted" />
                              </Link>
                            ))}
                            {!isLocked && (
                              <Link
                                to={`/student/tutor?courseId=${courseId}&lessonId=${lesson.id}`}
                                className="inline-flex items-center gap-1.5 font-sans text-xs text-cinema-gold hover:text-white transition-colors ml-auto font-medium"
                                title="Ask AI Tutor about this lesson"
                              >
                                <Sparkles size={12} /> Ask AI Tutor
                              </Link>
                            )}
                          </div>

                          {/* Assignments — Phase 4C: full brief + rubric */}
                          {!isLocked && lessonAssignments.length > 0 && (
                            <div className="mt-5 px-4 sm:px-0 sm:pl-10 space-y-4">
                              {lessonAssignments.map((assignment) => {
                                const isCapstone = isCapstoneAssignment(assignment)
                                const isGraded = assignment.submission_id && assignment.score !== null && assignment.score !== undefined
                                const isSubmitted = assignment.submission_id && !isGraded
                                return (
                                  <div
                                    key={assignment.id}
                                    className={`cinema-glass border p-4 sm:p-5 ${
                                      isCapstone
                                        ? 'border-cinema-gold/30 bg-cinema-gold/[0.03]'
                                        : 'border-cinema-glow/20'
                                    }`}
                                  >
                                    {/* Assignment header */}
                                    <div className="flex items-start gap-3 mb-3">
                                      {isCapstone
                                        ? <Flag size={16} className="text-cinema-gold mt-0.5 shrink-0" />
                                        : assignment.submission_id
                                        ? <CheckCircle size={16} className="text-green-400 mt-0.5 shrink-0" />
                                        : <Target size={16} className="text-cinema-glow mt-0.5 shrink-0" />}
                                      <div className="flex-1 min-w-0">
                                        {isCapstone && (
                                          <span className="inline-block px-2 py-0.5 text-xs font-sans font-semibold uppercase tracking-wider bg-cinema-gold/10 text-cinema-gold border border-cinema-gold/30 rounded-sm mb-2">
                                            Final Capstone
                                          </span>
                                        )}
                                        <h4 className={`font-display ${isCapstone ? 'text-base sm:text-lg text-cinema-gold' : 'text-sm sm:text-base text-white'} font-medium mb-1`}>
                                          {assignment.title}
                                        </h4>
                                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs font-sans text-cinema-muted font-medium">
                                          {assignment.assignment_type && (
                                            <span className="uppercase tracking-wide">{assignment.assignment_type}</span>
                                          )}
                                          <span>Max: {assignment.max_score} pts</span>
                                          {assignment.due_date && (
                                            <span className="flex items-center gap-1"><Calendar size={11} /> {new Date(assignment.due_date).toLocaleDateString()}</span>
                                          )}
                                          {assignment.file_format && (
                                            <span className="flex items-center gap-1"><FileText size={11} /> {assignment.file_format}</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Assignment brief / description */}
                                    {assignment.description && (
                                      <div className="mb-4 max-w-[850px]">
                                        <p className="cinema-label mb-2">Assignment Brief</p>
                                        <MarkdownContent content={assignment.description} />
                                      </div>
                                    )}

                                    {/* Learner-facing pre-submission checklist (trainer rubric is never exposed) */}
                                    {assignment.learner_checklist && (
                                      <div className="mb-4 pl-3 border-l border-cinema-glow/20 max-w-[850px]">
                                        <p className="cinema-label mb-2 text-cinema-glow">Before You Submit</p>
                                        <MarkdownContent content={assignment.learner_checklist} />
                                      </div>
                                    )}

                                    {/* Submission status + CTA */}
                                    <div className="pt-3 border-t border-white/5">
                                      {isGraded ? (
                                        <div className="space-y-2">
                                          <div className="flex flex-wrap items-center gap-3">
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-sans font-medium uppercase tracking-wide bg-green-500/10 text-green-400 border border-green-500/25 rounded-sm">
                                              <CheckCircle size={13} /> Graded
                                            </span>
                                            <span className="font-display text-lg text-green-400 font-semibold">
                                              {assignment.score} / {assignment.max_score}
                                            </span>
                                            {assignment.max_score > 0 && (
                                              <span className="font-sans text-xs text-cinema-muted font-medium">
                                                ({Math.round((assignment.score / assignment.max_score) * 100)}%)
                                              </span>
                                            )}
                                          </div>
                                          {assignment.feedback && (
                                            <div className="pl-3 border-l-2 border-blue-500/40">
                                              <p className="font-sans text-xs text-blue-400 uppercase tracking-wider font-semibold mb-1">Feedback</p>
                                              <p className="font-sans text-sm text-cinema-soft leading-relaxed">{assignment.feedback}</p>
                                            </div>
                                          )}
                                        </div>
                                      ) : isSubmitted ? (
                                        <div className="flex flex-wrap items-center gap-3">
                                          <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-sans font-medium uppercase tracking-wide bg-blue-500/10 text-blue-400 border border-blue-500/25 rounded-sm">
                                            Submitted
                                          </span>
                                          <span className="font-sans text-xs text-cinema-muted">
                                            {new Date(assignment.submitted_at).toLocaleDateString()}
                                          </span>
                                          <button
                                            onClick={() => { setSelectedAssignment(assignment); setSubmissionData({ submission_url: '', submission_text: '' }); setShowSubmitModal(true) }}
                                            className="inline-flex items-center gap-1.5 font-sans text-xs text-cinema-glow hover:text-white transition-colors font-medium min-h-[36px]"
                                          >
                                            <Upload size={13} /> Resubmit
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          onClick={() => { setSelectedAssignment(assignment); setSubmissionData({ submission_url: '', submission_text: '' }); setShowSubmitModal(true) }}
                                          className="inline-flex items-center gap-2 px-4 py-2.5 cinema-glass border border-cinema-glow/30 text-cinema-glow hover:bg-cinema-glow/10 text-xs sm:text-sm font-sans font-medium transition-all min-h-[36px]"
                                        >
                                          <Upload size={14} /> Submit Assignment
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}

                          {/* Phase 4B: Previous / Next lesson navigation */}
                          {!isLocked && (() => {
                            const prevLesson = getAdjacentLesson(lesson.id, 'prev')
                            const nextLesson = getAdjacentLesson(lesson.id, 'next')
                            const nextIsLocked = nextLesson && nextLesson.is_unlocked === false
                            if (!prevLesson && !nextLesson && !isCourseComplete) return null
                            return (
                              <div className="mt-5 px-4 sm:px-0 sm:pl-10 flex items-center justify-between gap-3 pt-4 border-t border-white/5">
                                {/* Previous */}
                                <div className="flex-1 min-w-0">
                                  {prevLesson ? (
                                    <button
                                      onClick={() => navigateToLesson(prevLesson)}
                                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-sans font-medium cinema-glass border border-white/8 text-cinema-muted hover:text-white hover:border-white/20 transition-all min-h-[36px]"
                                    >
                                      <ArrowLeft size={14} />
                                      <span className="hidden sm:inline">Previous</span>
                                      <span className="sm:hidden">Prev</span>
                                    </button>
                                  ) : (
                                    <span className="text-xs font-sans text-cinema-muted/40 px-3 py-2">
                                      First lesson
                                    </span>
                                  )}
                                </div>

                                {/* Next or Course Complete */}
                                <div className="flex-1 flex justify-end min-w-0">
                                  {nextLesson ? (
                                    nextIsLocked ? (
                                      <span className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-sans text-cinema-muted/50">
                                        <Lock size={12} /> Pay tuition to unlock next
                                      </span>
                                    ) : (
                                      <button
                                        onClick={() => navigateToLesson(nextLesson)}
                                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-sans font-medium cinema-glass border border-cinema-glow/25 text-cinema-glow hover:bg-cinema-glow/10 hover:border-cinema-glow/40 transition-all min-h-[36px]"
                                      >
                                        Next Lesson <ArrowRight size={14} />
                                      </button>
                                    )
                                  ) : isCourseComplete ? (
                                    <Link
                                      to="/student/certificates"
                                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-sans font-medium cinema-glass border border-green-500/30 text-green-400 hover:bg-green-500/10 transition-all min-h-[36px]"
                                    >
                                      <Award size={14} /> View Certificate
                                    </Link>
                                  ) : (
                                    <span className="text-xs font-sans text-cinema-muted/40 px-3 py-2">
                                      Last lesson
                                    </span>
                                  )}
                                </div>
                              </div>
                            )
                          })()}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Submit Modal */}
      {showSubmitModal && selectedAssignment && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="cinema-glass-strong border border-white/10 rounded-sm w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/8">
              <div>
                <h2 className="font-display text-base text-white font-light">Submit Assignment</h2>
                <p className="font-sans text-xs text-cinema-muted mt-0.5">{selectedAssignment.title}</p>
              </div>
              <button onClick={() => { setShowSubmitModal(false); setSelectedAssignment(null) }} className="text-cinema-muted hover:text-white transition-colors">
                <ArrowLeft size={16} className="rotate-[-135deg]" />
              </button>
            </div>
            <form onSubmit={handleSubmitAssignment} className="p-6 space-y-4">
              <div>
                <label className="cinema-label block mb-2">Submission URL</label>
                <input
                  type="url"
                  className={inputCls}
                  placeholder="https://drive.google.com/..."
                  value={submissionData.submission_url}
                  onChange={(e) => setSubmissionData(prev => ({ ...prev, submission_url: e.target.value }))}
                />
                <p className="font-sans text-xs text-cinema-muted mt-1">Link to your uploaded work (Google Drive, Dropbox, etc.)</p>
              </div>
              <div>
                <label className="cinema-label block mb-2">Notes / Description</label>
                <textarea
                  className={`${inputCls} resize-none`}
                  rows={4}
                  placeholder="Add any notes about your submission..."
                  value={submissionData.submission_text}
                  onChange={(e) => setSubmissionData(prev => ({ ...prev, submission_text: e.target.value }))}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowSubmitModal(false); setSelectedAssignment(null) }}
                  className="cinema-btn-ghost text-xs px-5 py-2.5 min-h-0 flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || (!submissionData.submission_url && !submissionData.submission_text)}
                  className="cinema-btn-primary text-xs py-2.5 min-h-0 flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Upload size={13} /> {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
